import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { intl } from '@trello/i18n';
import { Icon } from '@trello/nachos/icon';
import { CheckIcon } from '@trello/nachos/icons/check';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, usePopover } from '@trello/nachos/popover';
import { PopoverMenu, PopoverMenuButton } from '@trello/nachos/popover-menu';
import { useSharedState } from '@trello/shared-state';

import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { useBoardSwitcher } from './useBoardSwitcher';

import styles from './WorkspaceTabsMenuButton.less';

const MaybeCheckIcon = ({ condition }: { condition: boolean }) => {
  if (condition) {
    return <CheckIcon size="small" />;
  }
  return (
    <Icon
      size="small"
      glyph={() => <span />}
      dangerous_className={styles.blankIcon}
    />
  );
};

const GuestWorkspaceDisplayName: FunctionComponent<{
  workspaceId: string;
}> = ({ workspaceId }) => {
  const { data } = useBoardSwitcherWorkspaceFragment({
    from: { id: workspaceId },
    returnPartialData: true,
  });

  return (
    data?.displayName ??
    data?.name ?? (
      <FormattedMessage
        id="templates.board_switcher.unknown-workspace"
        defaultMessage="Unknown workspace"
        description="Title of an unknown workspace on the board switcher"
      />
    )
  );
};

export const WorkspaceTabsMenuButton: FunctionComponent = () => {
  const { workspaces, guestWorkspaceIds } = useBoardSwitcher();
  const [{ selectedWorkspaceId }] = useSharedState(boardSwitcherSharedState);

  const { hide, toggle, triggerRef, popoverProps } = usePopover({
    placement: 'bottom-end',
  });

  const handleOpenPopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspaceTabsMenuButton',
      source: 'boardSwitcherModal',
    });

    toggle();
  }, [toggle]);

  const onClick = useCallback(
    (id: string | null) => {
      Analytics.sendClickedButtonEvent({
        buttonName: 'workspaceTabsMenuItemButton',
        source: 'boardSwitcherModal',
        attributes: {
          workspaceFilter: id ?? 'all',
        },
      });

      hide();
      boardSwitcherSharedState.setValue({
        selectedWorkspaceId: id,
      });
    },
    [hide],
  );

  return (
    <>
      <button
        title={intl.formatMessage({
          id: 'templates.board_switcher.select-a-workspace',
          defaultMessage: 'Select a Workspace',
          description:
            'Title on button that opens a popover to select a workspace on the board switcher',
        })}
        className={styles.menuButton}
        ref={triggerRef}
        onClick={handleOpenPopover}
      >
        <DownIcon size="small" />
      </button>
      <Popover {...popoverProps}>
        <PopoverMenu>
          <PopoverMenuButton
            onClick={() => onClick(null)}
            iconBefore={<MaybeCheckIcon condition={!selectedWorkspaceId} />}
          >
            <FormattedMessage
              id="templates.board_switcher.all"
              defaultMessage="All"
              description="Text on the all workspaces button on the workspace popover menu"
            />
          </PopoverMenuButton>
          {workspaces.map(({ id, displayName }) => (
            <PopoverMenuButton
              key={id}
              onClick={() => onClick(id)}
              iconBefore={
                <MaybeCheckIcon condition={selectedWorkspaceId === id} />
              }
            >
              {displayName}
            </PopoverMenuButton>
          ))}
          {guestWorkspaceIds.map((id) => (
            <PopoverMenuButton
              key={id}
              onClick={() => onClick(id)}
              iconBefore={
                <MaybeCheckIcon condition={selectedWorkspaceId === id} />
              }
            >
              <GuestWorkspaceDisplayName workspaceId={id} />
            </PopoverMenuButton>
          ))}
        </PopoverMenu>
      </Popover>
    </>
  );
};
